import type { ActivitySurface } from './activityCatalog'
import type { DiscordActivityContext } from './discordSdk'
import type {
  SurfaceDataItem,
  SurfaceDataPayload,
  SurfaceDataView
} from './activitySurfaceData'

const apiBase = import.meta.env.VITE_ACTIVITY_SURFACE_DATA_URL as string | undefined

export async function loadActivitySurfaceData(
  surface: ActivitySurface,
  context: DiscordActivityContext
): Promise<SurfaceDataPayload | null> {
  if (!context.accessToken) return null

  const url = buildSurfaceDataUrl(apiBase, surface.key, context)
  if (!url) return null

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 2500)

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${context.accessToken}`
      }
    })
    if (!response.ok) return null
    return normalizeSurfaceData(await response.json(), surface.key)
  } catch {
    return null
  } finally {
    window.clearTimeout(timeout)
  }
}

function buildSurfaceDataUrl(
  base: string | undefined,
  surfaceKey: string,
  context: DiscordActivityContext
): URL | null {
  const trimmed = base?.trim()
  const withContext = (url: URL): URL => {
    url.searchParams.set('surface', surfaceKey)
    if (context.guildId) url.searchParams.set('guildId', context.guildId)
    if (context.channelId) url.searchParams.set('channelId', context.channelId)
    return url
  }

  if (!trimmed) {
    return import.meta.env.PROD
      ? withContext(new URL('/api/activity/surface-data', window.location.origin))
      : null
  }

  try {
    if (trimmed.startsWith('/')) {
      return withContext(new URL(trimmed, window.location.origin))
    }
    const url = new URL(trimmed)
    const localHost =
      url.hostname === 'localhost' ||
      url.hostname === '127.0.0.1' ||
      url.hostname === '[::1]'
    if (url.protocol !== 'https:' && !localHost) return null
    return withContext(url)
  } catch {
    return null
  }
}

function normalizeSurfaceData(
  input: unknown,
  expectedKey: string
): SurfaceDataPayload | null {
  const root = record(input)
  if (!root || root.version !== 1 || root.key !== expectedKey) return null

  const views = normalizeViews(root.views)
  if (!views.length) return null

  return {
    key: expectedKey,
    source: root.source === 'bot-runtime' ? 'bot-runtime' : 'static',
    generatedAt: numberValue(root.generatedAt),
    headline: cleanText(root.headline, 'Superficie runtime carregada.', 180),
    health: cleanText(root.health, 'Runtime conectado.', 160),
    views,
    safeguards: normalizeStringList(root.safeguards, 6, 100),
    nextBackend: normalizeStringList(root.nextBackend, 6, 100)
  }
}

function normalizeViews(input: unknown): SurfaceDataView[] {
  if (!Array.isArray(input)) return []
  return input.slice(0, 6).flatMap((viewInput, index) => {
    const view = record(viewInput)
    const items = normalizeItems(view?.items)
    if (!view || !items.length) return []
    return [{
      key: cleanKey(view.key, `view-${index + 1}`),
      title: cleanText(view.title, `View ${index + 1}`, 48),
      summary: cleanText(view.summary, 'Dados runtime da superficie.', 140),
      items
    }]
  })
}

function normalizeItems(input: unknown): SurfaceDataItem[] {
  if (!Array.isArray(input)) return []
  return input.slice(0, 24).flatMap((itemInput, index) => {
    const item = record(itemInput)
    if (!item) return []
    return [{
      title: cleanText(item.title, `Item ${index + 1}`, 80),
      meta: cleanText(item.meta, 'runtime', 80),
      status: cleanText(item.status, 'ok', 40),
      detail: cleanText(item.detail, 'Dado carregado do runtime.', 180)
    }]
  })
}

function normalizeStringList(input: unknown, limit: number, maxLength: number): string[] {
  if (!Array.isArray(input)) return []
  return input
    .slice(0, limit)
    .map((item) => cleanText(item, '', maxLength))
    .filter(Boolean)
}

function cleanKey(input: unknown, fallback: string): string {
  if (typeof input !== 'string') return fallback
  const cleaned = input.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 40)
  return cleaned || fallback
}

function cleanText(input: unknown, fallback: string, maxLength: number): string {
  if (typeof input !== 'string') return fallback
  const cleaned = input
    .replace(/@everyone|@here/gi, '[mention removida]')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength)
  return cleaned || fallback
}

function numberValue(input: unknown): number | undefined {
  return typeof input === 'number' && Number.isFinite(input)
    ? Math.floor(input)
    : undefined
}

function record(input: unknown): Record<string, unknown> | null {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : null
}
